import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getAcceptTermsText, logException } from 'Utils';
import InputField from 'Components/InputField';
import ProgressBar from 'Components/ProgressBar';
import TermsPanel from 'Components/TermsPanel';
import Validator from 'Components/Validator';

const initValidator = (strings) =>
  new Validator({
    email: [
      {
        message: strings.isRequired.formatUimTranslation(strings.email),
        method: 'isEmpty',
        validWhen: false,
      },
      {
        message: strings.validEmail,
        method: 'isEmail',
        validWhen: true,
      },
    ],
    firstName: [
      {
        message: strings.isRequired.formatUimTranslation(strings.firstName),
        method: 'isEmpty',
        validWhen: false,
      },
    ],
    lastName: [
      {
        message: strings.isRequired.formatUimTranslation(strings.lastName),
        method: 'isEmpty',
        validWhen: false,
      },
    ],
    consent: [
      {
        message: strings.isRequired.formatUimTranslation(
          strings.termsAndConditions
        ),
        method: 'isBoolean',
        validWhen: true,
      },
    ],
  });

const UpdateRequired = ({ country, locale, redirect, strings, uim }) => {
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [registrationItems, setRegistrationItems] = useState(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const validator = initValidator(strings);
  const validForm = validator.validate({
    email,
    firstName,
    lastName,
    consent,
  }).isValid;

  const handleChange = (event) => {
    event.preventDefault();
    const updateMethods = {
      firstName: setFirstName,
      lastName: setLastName,
      email: setEmail,
    };

    updateMethods[event.target.name](event.target.value);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    // handle actual form submission here
    if (validForm) updateProfileAndConsent();
  };

  const isValid = (value, { target }) => {
    return validator.isValid(target.name, value, {
      email,
      firstName,
      lastName,
    });
  };

  const updateProfileAndConsent = async () => {
    try {
      setErrorMessage('');
      const profile = { email, first_name: firstName, last_name: lastName };
      const { newsletters, policies } = uim.getSelectedRegistrationItems(
        registrationItems
      );
      await uim.uimApi.submitMandatoryAndNewsletterInformation(
        profile,
        policies,
        newsletters
      );
      redirect();
    } catch (err) {
      logException(err);
      setErrorMessage(err.message);
    }
  };

  const setConsentValidity = (valid, registrationItems) => {
    setConsent(valid);
    setRegistrationItems(registrationItems);
  };

  const loadPolicies = async () => {
    const registrationItems = await uim.fetchRegistrationItems(country);
    const { email, first_name, last_name } = uim.getProfileValues();
    setRegistrationItems(registrationItems);
    setEmail(email ?? '');
    setFirstName(first_name ?? '');
    setLastName(last_name ?? '');
  };

  useEffect(loadPolicies, []);

  return (
    <div>
      {submitting && <ProgressBar />}
      <form onSubmit={handleFormSubmit} className="email-form" noValidate>
        <h1 className="page-heading">{strings.emailHeading}</h1>
        {errorMessage && <p className="error-text">{errorMessage}</p>}
        <InputField
          className="form-control"
          disabled={submitting}
          id="email"
          isValid={isValid}
          label={strings.email}
          name="email"
          onChange={handleChange}
          value={email}
        />
        <div className="column--double">
          <InputField
            className="form-control"
            disabled={submitting}
            id="firstName"
            isValid={isValid}
            label={strings.firstName}
            name="firstName"
            onChange={handleChange}
            value={firstName}
          />
          <InputField
            className="form-control"
            disabled={submitting}
            id="lastName"
            isValid={isValid}
            label={strings.lastName}
            name="lastName"
            onChange={handleChange}
            value={lastName}
          />
        </div>

        {registrationItems && (
          <TermsPanel
            disabled={submitting}
            locale={locale}
            registrationItems={registrationItems}
            setConsentValidity={setConsentValidity}
            uim={uim}
          />
        )}

        <button type="submit" disabled={!validForm || submitting}>
          {getAcceptTermsText(strings, country)}
        </button>
      </form>
    </div>
  );
};

UpdateRequired.propTypes = {
  country: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  redirect: PropTypes.func.isRequired,
  strings: PropTypes.object.isRequired,
  uim: PropTypes.object.isRequired,
};

UpdateRequired.defaultProps = {};

export default UpdateRequired;
